import { useEffect, useState } from "react";
import AccountButton from "./AccountButton";
import AccountInput from "./AccountInput";
import AccountSection from "./AccountSection";
import { getContactInfo, updateContactInfo } from "./api/apiFunctions";
import { t } from "./i18n";

export default function ContactInfoPage({ lang = "en", goToTab }) {
  const [form, setForm] = useState({
    primaryPhone: "",
    mobilePhone: "",
    address1: "",
    address2: "",
    city: "",
    postalCode: "",
    state: "",
    country: "",
    emailAlerts: false,
    smsNotifications: false,
    newsletter: false,
  });

  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch contact info on component mount
  useEffect(() => {
    const fetchContactData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Get token from localStorage
        const token =
          localStorage.getItem("authToken") ||
          sessionStorage.getItem("authToken");

        if (!token) {
          throw new Error(
            "Authentication token not found. Please login again.",
          );
        }

        // Fetch contact data from API
        const response = await getContactInfo(token);

        if (response.success && response.data) {
          setForm({
            primaryPhone: response.data.primaryPhoneNumber || "",
            mobilePhone: response.data.mobilePhone || "",
            address1: response.data.addressLine1 || "",
            address2: response.data.addressLine2 || "",
            city: response.data.city || "",
            postalCode: response.data.postalCode || "",
            state: response.data.state || "",
            country: response.data.country || "",
            emailAlerts:
              response.data.receiveEmailAlerts === true ||
              response.data.receiveEmailAlerts === "true",
            smsNotifications:
              response.data.receiveTextMessages === true ||
              response.data.receiveTextMessages === "true",
            newsletter:
              response.data.subscribeToNewsletter === true ||
              response.data.subscribeToNewsletter === "true",
          });
        } else {
          throw new Error("Failed to fetch contact data");
        }
      } catch (error) {
        console.error("Error fetching contact data:", error);
        // setErrors({
        //   primaryPhone:
        //     error.message || "Failed to load contact data. Please try again.",
        // });
      } finally {
        setLoading(false);
      }
    };

    fetchContactData();
  }, []);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setErrors({});
      setSuccess(null);

      // Get token from localStorage
      const token =
        localStorage.getItem("authToken") ||
        sessionStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Prepare contact data for update
      const contactData = {
        primaryPhoneNumber: form.primaryPhone,
        mobilePhone: form.mobilePhone,
        addressLine1: form.address1,
        addressLine2: form.address2,
        city: form.city,
        postalCode: form.postalCode,
        state: form.state,
        country: form.country,
        receiveEmailAlerts: form.emailAlerts.toString(),
        receiveTextMessages: form.smsNotifications.toString(),
        subscribeToNewsletter: form.newsletter.toString(),
      };

      // Update contact information
      const response = await updateContactInfo(contactData, token);

      if (!response.success) {
        // Handle specific contact update errors
        const contactErrors = {};
        const message =
          response.message || "Failed to update contact information";

        if (message.toLowerCase().includes("phone")) {
          contactErrors.primaryPhone = message;
        } else if (message.toLowerCase().includes("address")) {
          contactErrors.address1 = message;
        } else if (message.toLowerCase().includes("city")) {
          contactErrors.city = message;
        } else if (
          message.toLowerCase().includes("postal") ||
          message.toLowerCase().includes("zip")
        ) {
          contactErrors.postalCode = message;
        } else {
          // Default to primary phone field
          contactErrors.primaryPhone = message;
        }

        setErrors(contactErrors);
        return false; // Return false to indicate failure
      }

      // Show success message
      setSuccess("Contact information updated successfully!");
      return true; // Return true to indicate success
    } catch (error) {
      // setErrors({
      //   primaryPhone:
      //     error.message ||
      //     "Failed to save contact information. Please try again.",
      // });
      return false; // Return false to indicate failure
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));

    // Clear success message when user starts typing
    if (success) {
      setSuccess(null);
    }
    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="h-screen bg-[#f7f8fa] flex flex-col items-center justify-center pb-8">
        <div className="w-full max-w-4xl">
          <div className="bg-white rounded-xl shadow p-8 mt-4">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4962d8]"></div>
              <span className="ml-4 text-lg text-[#2d348f]">
                Loading contact information...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fa] flex flex-col items-center p-4 md:p-8">
      <div className="w-full max-w-4xl">
        <div className="bg-white rounded-xl shadow p-8 mt-4">
          <AccountSection>
            {t("contactInfo", lang) || "Contact Info"}
          </AccountSection>
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="text-green-600 text-sm">✅ {success}</div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <AccountInput
                label={t("primaryPhone", lang) || "Primary Phone"}
                name="primaryPhone"
                value={form.primaryPhone}
                onChange={handleChange}
                isPhoneInput
              />
              {errors.primaryPhone && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.primaryPhone}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("mobilePhone", lang) || "Mobile Phone"}
                name="mobilePhone"
                value={form.mobilePhone}
                onChange={handleChange}
                isPhoneInput
              />
              {errors.mobilePhone && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.mobilePhone}
                </div>
              )}
            </div>
          </div>
          <AccountSection>{t("address", lang) || "Address"}</AccountSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <AccountInput
                label={t("address1", lang) || "Address Line 1"}
                name="address1"
                value={form.address1}
                onChange={handleChange}
              />
              {errors.address1 && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.address1}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("address2", lang) || "Address Line 2"}
                name="address2"
                value={form.address2}
                onChange={handleChange}
              />
              {errors.address2 && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.address2}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("city", lang) || "City"}
                name="city"
                value={form.city}
                onChange={handleChange}
              />
              {errors.city && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.city}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("postalCode", lang) || "Postal Code"}
                name="postalCode"
                value={form.postalCode}
                onChange={handleChange}
              />
              {errors.postalCode && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.postalCode}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("state", lang) || "State"}
                name="state"
                value={form.state}
                onChange={handleChange}
              />
              {errors.state && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.state}
                </div>
              )}
            </div>
            <div>
              <AccountInput
                label={t("country", lang) || "Country"}
                name="country"
                value={form.country}
                onChange={handleChange}
              />
              {errors.country && (
                <div className="text-red-500 text-sm mt-1">
                  ⚠️ {errors.country}
                </div>
              )}
            </div>
          </div>
          <AccountSection>
            {t("preferences", lang) || "Preferences"}
          </AccountSection>
          <div className="flex flex-col md:flex-row gap-8 mb-8">
            <label className="flex items-center gap-2 text-sm font-medium text-[#837f90]">
              <input
                type="checkbox"
                name="emailAlerts"
                checked={form.emailAlerts}
                onChange={handleChange}
                className="accent-[#4962d8]"
              />
              {t("receiveEmailAlerts", lang) || "Receive Email Alerts"}
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-[#837f90]">
              <input
                type="checkbox"
                name="smsNotifications"
                checked={form.smsNotifications}
                onChange={handleChange}
                className="accent-[#4962d8]"
              />
              {t("receiveSmsNotifications", lang) ||
                "Receive SMS notifications"}
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-[#837f90]">
              <input
                type="checkbox"
                name="newsletter"
                checked={form.newsletter}
                onChange={handleChange}
                className="accent-[#4962d8]"
              />
              {t("subscribeNewsletter", lang) || "Subscribe to newsletter"}
            </label>
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <AccountButton
              className="md:w-1/3"
              disabled={isSaving}
              onClick={() => goToTab("prev")}
            >
              <span className="mr-2">&laquo;</span> Back
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </AccountButton>
            <AccountButton
              className="md:w-1/3"
              onClick={() => goToTab("next")}
              disabled={isSaving}
            >
              <span className="ml-2">Next &raquo;</span>
            </AccountButton>
          </div>
        </div>
      </div>
    </div>
  );
}
